import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Wallet, Calculator, RotateCcw, Receipt, Weight, IndianRupee, BadgePercent, Fuel,
  ArrowRight, Check, ShieldCheck, Zap,
} from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, SectionHead, GradientCta, fadeUp } from "../components/marketing/Blocks";
import PricingCalculator from "../components/home/PricingCalculator";

const STEPS = [
  { n: "01", icon: Wallet, title: "Recharge Your Wallet", desc: "Top up your prepaid ShipAny wallet securely via Razorpay — UPI, cards, netbanking. No subscription, no lock-in." },
  { n: "02", icon: Calculator, title: "Compare Live Rates", desc: "Enter pickup pincode, delivery pincode and weight. Every enabled courier on that lane is quoted instantly with the final GST-inclusive price." },
  { n: "03", icon: Zap, title: "Book & Ship", desc: "The quoted amount is deducted from your wallet only when you book. AWB and shipping label are generated instantly." },
];

const INCLUDES = [
  { icon: Weight, title: "Base freight", desc: "Zone-aware pricing by chargeable weight slab — the higher of actual vs volumetric weight." },
  { icon: IndianRupee, title: "Additional weight", desc: "Per-kg charges beyond the base slab, shown transparently in every quote." },
  { icon: BadgePercent, title: "COD fee", desc: "A clear COD fee is shown upfront whenever you choose Cash on Delivery." },
  { icon: Fuel, title: "Fuel surcharge", desc: "Courier fuel surcharges are already included in the quoted price." },
  { icon: Receipt, title: "GST", desc: "Every quote shows the final price inclusive of GST before you book — no surprises later." },
];

const REFUNDS = [
  { icon: RotateCcw, title: "Cancel before pickup", desc: "Cancelled shipments are refunded in full to your wallet as soon as the courier confirms cancellation — most confirmations are instant." },
  { icon: ShieldCheck, title: "Billing disputes", desc: "Raise a support ticket within 7 days of a charge and our team will review it against courier records." },
];

export default function Pricing() {
  return (
    <MarketingLayout>
      <div data-testid="pricing-page">
        <MarketingHero kicker="Pricing" title="Pay Only for What You Ship"
          sub="No subscriptions. No minimum volumes. No hidden fees. Recharge your wallet, compare live courier rates, and pay per shipment — the final price is always shown before you book.">
          <motion.div {...fadeUp} className="mt-7 flex flex-col gap-3 sm:flex-row">
            <a href="#calculator" data-testid="pricing-calc-cta-btn"
              className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Calculate Live Rates <Calculator className="h-4 w-4" />
            </a>
            <Link to="/signup" data-testid="pricing-signup-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/5">
              Sign Up Free <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </MarketingHero>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="pricing-how-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="How it works" title="Simple Wallet-Based Pricing" sub="One prepaid wallet powers every shipment across every courier partner." />
            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {STEPS.map((s, i) => (
                <motion.div key={s.n} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`pricing-step-${i + 1}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20">
                      <s.icon className="h-5 w-5 text-shipany-cyan" />
                    </span>
                    <span className="mono text-xs font-bold text-slate-600">{s.n}</span>
                  </div>
                  <h3 className="font-display mt-4 text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="calculator" className="scroll-mt-24 bg-shipany-navy py-20 sm:py-24" data-testid="pricing-calculator-section">
          <div className="mx-auto max-w-5xl px-5">
            <SectionHead kicker="Live rates" title="Calculate Your Shipping Cost"
              sub="Domestic and international — real quotes from every enabled courier partner, the same engine used at booking time." />
            <motion.div {...fadeUp} className="mt-10">
              <PricingCalculator />
            </motion.div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="pricing-includes-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Transparency" title="What Every Rate Includes" sub="Quoted prices are final — all components are bundled before you see the number." />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {INCLUDES.map((it, i) => (
                <motion.div key={it.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  data-testid={`pricing-include-${it.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#10B981]/15">
                    <it.icon className="h-5 w-5 text-[#10B981]" />
                  </span>
                  <div>
                    <h3 className="font-display text-sm font-bold text-white">{it.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">{it.desc}</p>
                  </div>
                </motion.div>
              ))}
              <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.16 }}
                data-testid="pricing-include-final-price"
                className="flex gap-4 rounded-2xl border border-[#FF9933]/40 bg-[#FF9933]/10 p-6">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FF9933]/20">
                  <Check className="h-5 w-5 text-[#FF9933]" />
                </span>
                <div>
                  <h3 className="font-display text-sm font-bold text-white">One final price</h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-300">What you see in the quote is exactly what is deducted from your wallet at booking.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-20 sm:py-24" data-testid="pricing-refunds-section">
          <div className="mx-auto max-w-5xl px-5">
            <SectionHead kicker="Fair billing" title="Cancellations & Refunds" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2">
              {REFUNDS.map((r, i) => (
                <motion.div key={r.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`pricing-refund-card-${i + 1}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-shipany-blue/30">
                    <r.icon className="h-5 w-5 text-shipany-cyan" />
                  </span>
                  <h3 className="font-display mt-4 text-lg font-bold text-white">{r.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{r.desc}</p>
                </motion.div>
              ))}
            </div>
            <motion.p {...fadeUp} className="mt-8 text-center text-xs text-slate-500" data-testid="pricing-refund-note">
              RTO shipments are billed per the courier's rate card; any refundable component is credited back to your wallet. See our <Link to="/refund-policy" data-testid="pricing-refund-policy-link" className="text-shipany-cyan hover:underline">Refund Policy</Link>.
            </motion.p>
          </div>
        </section>

        <GradientCta title="Start Shipping in Minutes" sub="Create your free account, recharge your wallet and book your first shipment today."
          primary={["Sign Up Free", "/signup"]} secondary={["Contact Us", "/contact"]} testId="pricing-final-cta" />
      </div>
    </MarketingLayout>
  );
}
